import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { money, toCents } from "./BillingWorkspace";

export default function PayAppDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [edit, setEdit] = useState({});
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase5/pay-apps/${id}`).then((r) => {
    setA(r.data);
    setEdit(Object.fromEntries(r.data.rows.map((x) => [x.line_id, { w: (x.work_this_period_cents / 100).toString(), s: (x.materials_stored_cents / 100).toString() }])));
  }).catch((e) => setError(errMsg(e, "Could not load."))), [id]);
  useEffect(() => { load(); }, [load]);
  const act = (p) => p.then(() => { setError(null); load(); }).catch((e) => setError(errMsg(e, "That did not work.")));
  const role = a?.role_key;
  const writer = ["OWNER_SUPER_ADMIN", "BOOKKEEPER_ACCOUNTANT"].includes(role);
  const founder = role === "OWNER_SUPER_ADMIN";
  const draft = a?.state === "draft" && writer;
  const save = () => act(api.patch(`/phase5/pay-apps/${id}`, { lines: Object.entries(edit).map(([line_id, v]) => ({ line_id, work_this_period_cents: toCents(v.w || 0), materials_stored_cents: toCents(v.s || 0) })) }));
  const pdf = () => api.get(`/phase5/pay-apps/${id}/pdf`, { responseType: "blob" }).then((r) => {
    const url = URL.createObjectURL(r.data);
    const link = document.createElement("a");
    link.href = url; link.download = `pay-app-${a.number}.pdf`; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }).catch((e) => setError(errMsg(e, "Could not download.")));
  return (
    <AdminAppShell activeNav="finance" user={user} title={a ? `Pay application No. ${a.number}` : "Pay application"} subtitle={a ? `Period to ${a.period_to} · ${a.state}` : ""}
      breadcrumbs={[{ label: "Financials", to: "/phase5/billing" }, { label: a ? `No. ${a.number}` : "Pay application", to: null }]}>
      <div data-testid="phase5-app-detail">
        {error && <p role="alert" className="mb-3 text-sm text-[#991B1B]" data-testid="phase5-app-error">{error}</p>}
        {a && (
          <Card className="ds-card border-erf-borderCream">
            <CardContent className="p-5 space-y-4 text-sm font-nav text-erf-dark">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left" data-testid="phase5-g703-table">
                  <caption className="sr-only">Continuation sheet</caption>
                  <thead><tr className="border-b border-erf-borderCream">
                    <th className="py-2 pr-2">Line</th><th className="pr-2">Scheduled</th><th className="pr-2">Previous</th>
                    <th className="pr-2">This period ($)</th><th className="pr-2">Stored ($)</th><th className="pr-2">%</th><th>Balance</th>
                  </tr></thead>
                  <tbody>
                    {a.rows.map((r) => (
                      <tr key={r.line_id} className="border-b border-erf-borderCream/60" data-testid={`phase5-row-${r.item_no}`}>
                        <td className="py-2 pr-2">{r.item_no}. {r.description}</td>
                        <td className="pr-2">{money(r.scheduled_value_cents)}</td>
                        <td className="pr-2">{money(r.previous_cents)}</td>
                        <td className="pr-2">{draft ? <Input aria-label={`This period, line ${r.item_no}`} inputMode="decimal" className="h-11 w-28" value={edit[r.line_id]?.w ?? ""} onChange={(e) => setEdit({ ...edit, [r.line_id]: { ...edit[r.line_id], w: e.target.value } })} data-testid={`phase5-work-${r.item_no}`} /> : money(r.work_this_period_cents)}</td>
                        <td className="pr-2">{draft ? <Input aria-label={`Stored materials, line ${r.item_no}`} inputMode="decimal" className="h-11 w-28" value={edit[r.line_id]?.s ?? ""} onChange={(e) => setEdit({ ...edit, [r.line_id]: { ...edit[r.line_id], s: e.target.value } })} data-testid={`phase5-stored-${r.item_no}`} /> : money(r.materials_stored_cents)}</td>
                        <td className="pr-2">{r.percent}%</td><td>{money(r.balance_to_finish_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <dl className="grid gap-1 sm:grid-cols-2" data-testid="phase5-g702-summary">
                <dt>Completed and stored to date</dt><dd data-testid="phase5-completed">{money(a.completed_to_date_cents)}</dd>
                <dt>Retention ({(a.retention_bps / 100).toFixed(2)}%)</dt><dd data-testid="phase5-retention">{money(a.retention_cents)}</dd>
                <dt>Less previous certificates</dt><dd>{money(a.previous_certificates_cents)}</dd>
                <dt className="font-semibold">Current payment due</dt><dd className="font-semibold" data-testid="phase5-due">{money(a.current_payment_due_cents)}</dd>
              </dl>
              <div className="flex flex-wrap gap-2">
                {draft && <Button variant="outline" className="h-11 border-erf-borderCream" onClick={save} data-testid="phase5-save-lines">Save amounts</Button>}
                {draft && <Button className="h-11 bg-erf-gold text-erf-dark hover:bg-erf-goldHover" onClick={() => act(api.post(`/phase5/pay-apps/${id}/submit`))} data-testid="phase5-submit">Submit for Founder approval</Button>}
                {founder && a.state === "submitted" && <Button className="h-11 bg-erf-dark text-[#FEFCF7]" onClick={() => act(api.post(`/phase5/pay-apps/${id}/approve`))} data-testid="phase5-approve">Approve</Button>}
                {founder && a.state === "submitted" && <Button variant="outline" className="h-11 border-erf-borderCream" onClick={() => act(api.post(`/phase5/pay-apps/${id}/return`))} data-testid="phase5-return">Return to draft</Button>}
                {writer && a.state === "approved" && <Button className="h-11 bg-erf-dark text-[#FEFCF7]" onClick={() => act(api.post(`/phase5/pay-apps/${id}/issue`))} data-testid="phase5-issue">Issue (create PDF record)</Button>}
                {a.state === "issued" && <Button variant="outline" className="h-11 border-erf-borderCream" onClick={pdf} data-testid="phase5-pdf">Download PDF</Button>}
              </div>
              <p className="text-xs text-erf-dark/80">Issuing locks this application and posts it to the ledger. It does not send anything or request payment.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminAppShell>
  );
}
